import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SessionStore } from '../../../core/auth/session.store';
import { TranslationService } from '../../../core/i18n/translation.service';
import { MultiSelectComponent } from '../../../components/multi-select/multi-select.component';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { ChecklistDetailDto, ChecklistGroupDto } from '../../checklists/models/checklist.models';
import { ChecklistsApi } from '../../checklists/services/checklists.api';
import {
  AuditAssignmentDto,
  AuditAnnualTargetDto,
  AuditDto,
  AuditFrequencyType,
  AuditLocationInput,
  CreateAuditAssignmentPayload,
  LocationDto,
  RoleDto,
} from '../models/audit.models';
import { AuditsApi } from '../services/audits.api';

@Component({
  selector: 'app-audit-creator',
  imports: [CommonModule, ConfirmationDialogComponent, MultiSelectComponent],
  templateUrl: './audit-creator.component.html',
  styleUrl: './audit-creator.component.css',
})
export class AuditCreatorComponent {
  private readonly checklistsApi = inject(ChecklistsApi);
  private readonly auditsApi = inject(AuditsApi);
  private readonly sessionStore = inject(SessionStore);
  private loadRequestId = 0;

  protected readonly i18n = inject(TranslationService);
  readonly sourceAuditId = input<string | null>(null);
  readonly closeRequested = output<void>();
  protected readonly currentStep = signal(1);
  protected readonly auditId = signal<string | null>(null);
  protected readonly auditName = signal('');
  protected readonly auditDescription = signal('');
  protected readonly selectedFrequency = signal<AuditFrequencyType | 'annual'>('weekly');
  protected readonly annualYear = signal(String(new Date().getFullYear()));
  protected readonly annualTargets = signal<AuditAnnualTargetDto[]>([]);
  protected readonly annualDrafts = signal<Record<string, string>>({});
  protected readonly annualValidationVisible = signal(false);
  protected readonly executionDate = signal('');
  protected readonly startTime = signal('00:00');
  protected readonly endTime = signal('23:59');
  protected readonly startDate = signal('');
  protected readonly endDate = signal('');
  protected readonly dayOfMonth = signal('');
  protected readonly selectedWeekdays = signal<Set<number>>(new Set([0, 2, 4]));
  protected readonly isSavingAudit = signal(false);
  protected readonly isSavingAuditLocations = signal(false);
  protected readonly isSavingAssignment = signal(false);
  protected readonly auditSaveError = signal('');
  protected readonly auditLocationsSaveError = signal('');
  protected readonly auditLocationsValidationError = signal('');
  protected readonly auditLocationsLoadError = signal('');
  protected readonly assignmentSaveError = signal('');
  protected readonly assignmentValidationError = signal('');
  protected readonly nameValidationVisible = signal(false);
  protected readonly checklistRequiredDialogOpen = signal(false);
  protected readonly isLoadingChecklists = signal(false);
  protected readonly checklistLoadError = signal('');
  protected readonly availableChecklists = signal<ChecklistDetailDto[]>([]);
  protected readonly selectedChecklistIds = signal<Set<string>>(new Set());
  protected readonly availableLocations = signal<LocationDto[]>([]);
  protected readonly availableRoles = signal<RoleDto[]>([]);
  protected readonly auditLocations = signal<AuditLocationInput[]>([]);
  protected readonly selectedLocationIds = signal<string[]>([]);
  protected readonly selectedRoleIds = signal<string[]>([]);
  protected readonly hasSavedAuditLocations = signal(false);
  protected readonly hasAvailableChecklists = computed(() => this.availableChecklists().length > 0);
  protected readonly hasAvailableLocations = computed(() => this.availableLocations().length > 0);
  protected readonly hasAvailableRoles = computed(() => this.availableRoles().length > 0);
  protected readonly hasSavedAudit = computed(() => Boolean(this.auditId()));
  protected readonly isEditingAudit = computed(() => Boolean(this.sourceAuditId()));
  protected readonly isDefinitionLocked = computed(() => this.hasSavedAudit() && !this.isEditingAudit());

  protected readonly weekdays = [
    { value: 0, labelKey: 'auditCreator.weekdays.mon', selected: true },
    { value: 1, labelKey: 'auditCreator.weekdays.tue', selected: false },
    { value: 2, labelKey: 'auditCreator.weekdays.wed', selected: true },
    { value: 3, labelKey: 'auditCreator.weekdays.thu', selected: false },
    { value: 4, labelKey: 'auditCreator.weekdays.fri', selected: true },
    { value: 5, labelKey: 'auditCreator.weekdays.sat', selected: false },
    { value: 6, labelKey: 'auditCreator.weekdays.sun', selected: false },
  ];

  constructor() {
    effect(() => {
      void this.initializeForm(this.sourceAuditId());
    });
  }

  protected goToStep(step: number): void {
    if (step > 1 && !this.hasSavedAudit()) {
      return;
    }
    if (step === 3 && !this.hasSavedAuditLocations()) {
      return;
    }

    this.currentStep.set(step);
  }

  protected updateAuditName(value: string): void {
    this.auditName.set(value);
    if (value.trim()) {
      this.nameValidationVisible.set(false);
    }
  }

  protected updateAuditDescription(value: string): void {
    this.auditDescription.set(value);
  }

  protected readonly locationOptions = computed(() => this.availableLocations().map(location => ({
    value: location.id, label: location.name,
  })));
  protected readonly roleOptions = computed(() => this.availableRoles().map(role => ({
    value: String(role.id), label: this.i18n.t('auth.account.roles.' + role.name),
  })));
  protected readonly pendingAuditLocations = computed(() => {
    const existing = new Set(this.auditLocations().map(item => item.location_id + ':' + item.role_id));
    const locations = this.availableLocations().filter(location => this.selectedLocationIds().includes(location.id));
    const roles = this.availableRoles().filter(role => this.selectedRoleIds().includes(String(role.id)));
    return locations.flatMap(location => roles
      .filter(role => !existing.has(location.id + ':' + role.id))
      .map(role => ({ location_id: location.id, role_id: role.id })));
  });

  protected updateSelectedLocations(values: string[]): void {
    this.selectedLocationIds.set(values);
    this.auditLocationsValidationError.set('');
  }

  protected updateSelectedRoles(values: string[]): void {
    this.selectedRoleIds.set(values);
    this.auditLocationsValidationError.set('');
  }

  protected addAuditLocation(): void {
    if (this.isSavingAuditLocations()) return;
    if (!this.selectedLocationIds().length || !this.selectedRoleIds().length) {
      this.auditLocationsValidationError.set(this.i18n.t('auditCreator.errors.locationAndRoleRequired'));
      return;
    }
    const additions = this.pendingAuditLocations();
    if (!additions.length) {
      this.auditLocationsValidationError.set(this.i18n.t('auditCreator.errors.allLocationRolesAssigned'));
      return;
    }
    this.auditLocations.update(current => [...current, ...additions]);
    this.selectedLocationIds.set([]);
    this.selectedRoleIds.set([]);
    this.hasSavedAuditLocations.set(false);
    this.auditLocationsValidationError.set('');
    this.auditLocationsSaveError.set('');
  }

  protected removeAuditLocation(index: number): void {
    this.auditLocations.update((current) => current.filter((_, itemIndex) => itemIndex !== index));
    this.hasSavedAuditLocations.set(false);
    this.auditLocationsValidationError.set('');
  }

  protected getLocationName(locationId: string): string {
    return this.availableLocations().find((location) => location.id === locationId)?.name ?? locationId;
  }

  protected getRoleName(roleId: number): string {
    return this.availableRoles().find((role) => role.id === roleId)?.name ?? String(roleId);
  }

  protected setFrequency(frequency: AuditFrequencyType | 'annual'): void {
    this.selectedFrequency.set(frequency);
    this.assignmentSaveError.set('');
    this.assignmentValidationError.set('');
  }

  protected updateAnnualYear(value: string): void {
    this.annualYear.set(value);
    this.assignmentValidationError.set('');
    this.assignmentSaveError.set('');
  }

  protected annualCount(item: AuditLocationInput): string {
    const key = `${this.annualYear()}:${item.location_id}:${item.role_id}`;
    const draft = this.annualDrafts()[key];
    if (draft !== undefined) return draft;
    const saved = this.annualTargets().find(row => row.year === Number(this.annualYear()) &&
      row.location_id === item.location_id && row.role_id === item.role_id);
    return saved ? String(saved.target_count) : '';
  }

  protected updateAnnualCount(item: AuditLocationInput, value: string): void {
    const key = `${this.annualYear()}:${item.location_id}:${item.role_id}`;
    this.annualDrafts.update(current => ({ ...current, [key]: value }));
    this.assignmentValidationError.set('');
    this.assignmentSaveError.set('');
  }

  protected annualCountInvalid(item: AuditLocationInput): boolean {
    const value = Number(this.annualCount(item));
    return !Number.isInteger(value) || value < 1 || value > 99;
  }

  private async saveAnnualPlan(auditId: string): Promise<void> {
    this.annualValidationVisible.set(true);
    const year = Number(this.annualYear());
    if (!Number.isInteger(year) || year < 1 || year > 9999) {
      this.assignmentValidationError.set(this.i18n.t('auditCreator.annual.invalidYear'));
      return;
    }
    if (!this.hasSavedAuditLocations() || !this.auditLocations().length ||
        this.auditLocations().some(item => this.annualCountInvalid(item))) {
      this.assignmentValidationError.set(this.i18n.t('auditCreator.annual.invalidCounts'));
      return;
    }
    this.isSavingAssignment.set(true);
    try {
      const response = await firstValueFrom(this.auditsApi.saveAnnualPlan(auditId, {
        year,
        targets: this.auditLocations().map(item => ({ ...item, target_count: Number(this.annualCount(item)) })),
      }));
      this.annualTargets.set(response.results);
      this.close();
    } catch (error) {
      this.assignmentSaveError.set(this.scheduleError(error, 'auditCreator.annual.saveError'));
    } finally {
      this.isSavingAssignment.set(false);
    }
  }

  private scheduleError(error: unknown, fallback: string): string {
    const code = error instanceof HttpErrorResponse ? error.error?.code : null;
    const key = code === 'audit_planning_mode_conflict' ? 'auditCreator.annual.modeConflict'
      : code === 'annual_plan_assignments_changed' ? 'auditCreator.annual.assignmentsChanged' : fallback;
    return this.i18n.t(key);
  }

  protected updateExecutionDate(value: string): void {
    this.executionDate.set(value);
    this.assignmentValidationError.set('');
  }

  protected updateStartDate(value: string): void {
    this.startDate.set(value);
    this.assignmentValidationError.set('');
  }

  protected updateEndDate(value: string): void {
    this.endDate.set(value);
    this.assignmentValidationError.set('');
  }

  protected updateDayOfMonth(value: string): void {
    this.dayOfMonth.set(value);
    this.assignmentValidationError.set('');
  }

  protected isWeekdaySelected(value: number): boolean {
    return this.selectedWeekdays().has(value);
  }

  protected toggleWeekday(value: number): void {
    this.selectedWeekdays.update((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
    this.assignmentValidationError.set('');
  }

  protected isChecklistSelected(checklistId: string): boolean {
    return this.selectedChecklistIds().has(checklistId);
  }

  protected toggleChecklist(checklistId: string): void {
    if (this.isDefinitionLocked()) {
      return;
    }

    this.selectedChecklistIds.update((current) => {
      const next = new Set(current);
      if (next.has(checklistId)) {
        next.delete(checklistId);
      } else {
        next.add(checklistId);
      }
      return next;
    });
  }

  protected async saveAuditAndContinue(): Promise<void> {
    this.auditSaveError.set('');

    if (this.isDefinitionLocked()) {
      this.currentStep.set(2);
      return;
    }

    const name = this.auditName().trim();
    if (!name) {
      this.nameValidationVisible.set(true);
      return;
    }

    const checklistIds = Array.from(this.selectedChecklistIds());
    if (!checklistIds.length) {
      this.checklistRequiredDialogOpen.set(true);
      return;
    }

    this.isSavingAudit.set(true);

    try {
      const saved = await this.saveAuditDefinition(name, checklistIds);
      this.auditId.set(saved.id);
      this.currentStep.set(2);
    } catch {
      const errorKey = this.isEditingAudit()
        ? 'auditCreator.errors.updateAudit'
        : 'auditCreator.errors.createAudit';
      this.auditSaveError.set(this.i18n.t(errorKey));
    } finally {
      this.isSavingAudit.set(false);
    }
  }

  protected async saveAssignment(): Promise<void> {
    if (this.isSavingAssignment() || this.isLoadingChecklists() || this.checklistLoadError()) return;
    this.assignmentSaveError.set('');
    this.assignmentValidationError.set('');

    const auditId = this.auditId();
    if (!auditId) {
      this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.missingSavedAudit'));
      return;
    }

    if (this.selectedFrequency() === 'annual') {
      await this.saveAnnualPlan(auditId);
      return;
    }
    const payload = this.buildAssignmentPayload();
    if (!payload) {
      return;
    }

    this.isSavingAssignment.set(true);

    try {
      await firstValueFrom(this.auditsApi.replaceAssignment(auditId, payload));
      this.close();
    } catch (error) {
      this.assignmentSaveError.set(this.scheduleError(error, 'auditCreator.errors.createAssignment'));
    } finally {
      this.isSavingAssignment.set(false);
    }
  }

  protected async saveAuditLocationsAndContinue(): Promise<void> {
    this.auditLocationsSaveError.set('');
    this.auditLocationsValidationError.set('');

    const auditId = this.auditId();
    if (!auditId) {
      this.auditLocationsValidationError.set(this.i18n.t('auditCreator.errors.missingSavedAudit'));
      return;
    }
    if (!this.auditLocations().length) {
      this.auditLocationsValidationError.set(
        this.i18n.t('auditCreator.errors.auditLocationRequired'),
      );
      return;
    }

    this.isSavingAuditLocations.set(true);
    try {
      const response = await firstValueFrom(
        this.auditsApi.replaceAuditLocations(auditId, { locations: this.auditLocations() }),
      );
      this.auditLocations.set(
        response.results.map(({ location_id, role_id }) => ({ location_id, role_id })),
      );
      this.hasSavedAuditLocations.set(true);
      this.currentStep.set(3);
    } catch {
      this.auditLocationsSaveError.set(this.i18n.t('auditCreator.errors.saveAuditLocations'));
    } finally {
      this.isSavingAuditLocations.set(false);
    }
  }

  protected closeChecklistRequiredDialog(): void {
    this.checklistRequiredDialogOpen.set(false);
  }

  protected close(): void {
    this.closeRequested.emit();
  }

  protected getChecklistQuestionsCount(checklist: ChecklistDetailDto): number {
    return (checklist.groups ?? []).reduce((total, group) => total + this.getGroupQuestionsCount(group), 0);
  }

  protected getGroupQuestionsCount(group: ChecklistGroupDto): number {
    return group.items?.length ?? 0;
  }

  protected formatCreatedAt(value: string): string {
    const locale = this.i18n.language() === 'pl' ? 'pl-PL' : 'en-US';

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(value));
  }

  private async initializeForm(auditId: string | null): Promise<void> {
    const requestId = ++this.loadRequestId;
    this.isLoadingChecklists.set(true);
    this.checklistLoadError.set('');
    this.auditSaveError.set('');
    this.auditLocationsSaveError.set('');
    this.auditLocationsValidationError.set('');
    this.auditLocationsLoadError.set('');
    this.assignmentSaveError.set('');
    this.assignmentValidationError.set('');
    this.nameValidationVisible.set(false);
    this.currentStep.set(1);
    this.selectedLocationIds.set([]);
    this.selectedRoleIds.set([]);
    this.annualTargets.set([]);
    this.annualDrafts.set({});
    this.annualYear.set(String(new Date().getFullYear()));
    this.annualValidationVisible.set(false);

    if (!auditId) {
      this.auditId.set(null);
      this.auditName.set('');
      this.auditDescription.set('');
      this.selectedChecklistIds.set(new Set());
      this.auditLocations.set([]);
      this.hasSavedAuditLocations.set(false);
      this.resetScheduleForm();
    }

    try {
      const [
        checklistsResponse,
        locationsResponse,
        rolesResponse,
        audit,
        auditLocationsResponse,
        assignmentsResponse,
        annualTargetsResponse,
      ] = await Promise.all([
        firstValueFrom(this.checklistsApi.getChecklistsFull()),
        firstValueFrom(this.auditsApi.getAuditAvailableLocations()),
        firstValueFrom(this.auditsApi.getAuditAvailableRoles()),
        auditId ? firstValueFrom(this.auditsApi.getAudit(auditId)) : Promise.resolve(null),
        auditId ? firstValueFrom(this.auditsApi.getAuditLocations(auditId)) : Promise.resolve(null),
        auditId ? firstValueFrom(this.auditsApi.getAssignments(auditId)) : Promise.resolve(null),
        auditId ? firstValueFrom(this.auditsApi.getAnnualTargets(auditId)) : Promise.resolve(null),
      ]);
      if (requestId !== this.loadRequestId) {
        return;
      }

      const selectedIds = new Set(audit?.checklists ?? []);
      const availableChecklists = checklistsResponse.results.filter((checklist) => {
        return checklist.status === 'active' || selectedIds.has(checklist.id);
      });

      this.availableChecklists.set(availableChecklists);
      const savedAuditLocations = (auditLocationsResponse?.results ?? []).map(
        ({ location_id, role_id }) => ({ location_id, role_id }),
      );
      const assignedLocationIds = new Set(
        savedAuditLocations.map(({ location_id }) => location_id),
      );
      this.availableLocations.set(
        locationsResponse.results.filter(
          (location) => location.status === 'active' || assignedLocationIds.has(location.id),
        ),
      );
      this.availableRoles.set(
        rolesResponse.results.filter((role) => role.name !== 'SUPER_ADMIN'),
      );
      this.auditLocations.set(savedAuditLocations);
      this.hasSavedAuditLocations.set(savedAuditLocations.length > 0);
      if (audit) {
        this.auditId.set(audit.id);
        this.auditName.set(audit.name);
        this.auditDescription.set(audit.description);
        this.selectedChecklistIds.set(selectedIds);
      }
      this.populateScheduleForm(assignmentsResponse?.results[0] ?? null);
      const annualTargets = (annualTargetsResponse?.results ?? []).filter(row => row.source === 'manual');
      this.annualTargets.set(annualTargets);
      if (annualTargets.length) {
        this.selectedFrequency.set('annual');
        const currentYear = new Date().getFullYear();
        this.annualYear.set(String(annualTargets.some(row => row.year === currentYear)
          ? currentYear : Math.max(...annualTargets.map(row => row.year))));
      }
    } catch {
      const errorKey = auditId
        ? 'auditCreator.errors.loadAudit'
        : 'auditCreator.errors.loadChecklists';
      this.checklistLoadError.set(this.i18n.t(errorKey));
      this.auditLocationsLoadError.set(this.i18n.t('auditCreator.errors.loadAuditLocations'));
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoadingChecklists.set(false);
      }
    }
  }

  private saveAuditDefinition(name: string, checklistIds: string[]): Promise<AuditDto> {
    const description = this.auditDescription().trim();
    const auditId = this.sourceAuditId();

    if (auditId) {
      return firstValueFrom(this.auditsApi.updateAudit(auditId, {
        name,
        description,
        checklists: checklistIds,
      }));
    }

    return firstValueFrom(this.auditsApi.createAudit({
      name,
      description,
      checklists: checklistIds,
    }));
  }

  private buildAssignmentPayload(): CreateAuditAssignmentPayload | null {
    const frequencyType = this.selectedFrequency();
    if (frequencyType === 'annual') return null;
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(this.startTime()) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(this.endTime()) || this.startTime() > this.endTime()) {
      this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.timeRange'));
      return null;
    }
    const frequency: Record<string, string | number | number[]> = {};

    if (frequencyType === 'one_time') {
      const executionDate = this.executionDate();
      if (!executionDate) {
        this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.executionDateRequired'));
        return null;
      }
      frequency['execution_date'] = executionDate;
    } else {
      const startDate = this.startDate();
      const endDate = this.endDate();
      if (!startDate) {
        this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.startDateRequired'));
        return null;
      }
      if (endDate && endDate < startDate) {
        this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.invalidDateRange'));
        return null;
      }

      frequency['start_date'] = startDate;
      if (endDate) {
        frequency['end_date'] = endDate;
      }
    }

    if (frequencyType === 'weekly' || frequencyType === 'custom_days') {
      const weekdays = Array.from(this.selectedWeekdays()).sort((a, b) => a - b);
      if (!weekdays.length) {
        this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.weekdaysRequired'));
        return null;
      }
      frequency['weekdays'] = weekdays;
    }

    if (frequencyType === 'monthly') {
      const dayOfMonth = Number(this.dayOfMonth());
      if (!Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
        this.assignmentValidationError.set(this.i18n.t('auditCreator.errors.dayOfMonthRequired'));
        return null;
      }
      frequency['day_of_month'] = dayOfMonth;
    }

    return {
      start_time: this.startTime(), end_time: this.endTime(),
      frequency_type: frequencyType,
      frequency,
      created_by: this.sessionStore.snapshot()?.userCode ?? null,
    };
  }

  private resetScheduleForm(): void {
    this.selectedFrequency.set('weekly');
    this.executionDate.set('');
    this.startTime.set('00:00');
    this.endTime.set('23:59');
    this.startDate.set('');
    this.endDate.set('');
    this.dayOfMonth.set('');
    this.selectedWeekdays.set(new Set([0, 2, 4]));
  }

  private populateScheduleForm(assignment: AuditAssignmentDto | null): void {
    this.resetScheduleForm();
    if (!assignment) {
      return;
    }

    this.startTime.set(assignment.start_time?.slice(0, 5) ?? "00:00");
    this.endTime.set(assignment.end_time?.slice(0, 5) ?? "23:59");
    this.selectedFrequency.set(assignment.frequency_type);
    const frequency = assignment.frequency;

    if (assignment.frequency_type === 'one_time') {
      this.executionDate.set(String(frequency['execution_date'] ?? ''));
      return;
    }

    this.startDate.set(String(frequency['start_date'] ?? ''));
    this.endDate.set(String(frequency['end_date'] ?? ''));

    if (assignment.frequency_type === 'weekly' || assignment.frequency_type === 'custom_days') {
      const weekdays = Array.isArray(frequency['weekdays']) ? frequency['weekdays'] : [];
      this.selectedWeekdays.set(new Set(weekdays.map((value) => Number(value))));
    }

    if (assignment.frequency_type === 'monthly') {
      this.dayOfMonth.set(String(frequency['day_of_month'] ?? ''));
    }
  }
}
